import { GroupeFormData } from "@repo/types";
import { Alert, AlertDescription } from "@repo/ui/components/ui/alert";
import { Button } from "@repo/ui/components/ui/button";
import { Card } from "@repo/ui/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/components/ui/tabs";
import type { SubscriptionConfig } from "@repo/ui/lib/api/services/settings.service";
import { Check, Info, Loader2, Package, Settings, X } from "lucide-react";
import { useMemo } from "react";
import { AuthService } from "@repo/ui/lib/api/services/auth.service";
import { GroupeAdminSection } from "./shared/groupe-admin-section";
import { GroupeInfoSection } from "./shared/groupe-info-section";
import { GroupePlanSection } from "./shared/groupe-plan-section";

interface EditGroupeFormFieldsProps {
  formData: GroupeFormData;
  error: string | null;
  isPending: boolean;
  subscriptionConfig?: SubscriptionConfig;
  selectedPlanType: 'BASIC' | 'ADVANCED';
  selectedTierId: string;
  groupeId?: number;
  isUpdatingSubscription: boolean;
  onCancel: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (name: string, value: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handlePlanSelect: (planType: 'BASIC' | 'ADVANCED', tierId: string) => void;
  handleSubscriptionUpdate: () => Promise<void>;
}

export function EditGroupeFormFields({
  formData,
  error,
  isPending,
  subscriptionConfig,
  selectedPlanType,
  selectedTierId,
  groupeId,
  isUpdatingSubscription,
  onCancel,
  handleInputChange,
  handleSelectChange,
  handleSubmit,
  handlePlanSelect,
  handleSubscriptionUpdate
}: EditGroupeFormFieldsProps) {

  const isAdmin = useMemo(() => new AuthService().isAdmin(), []);

  const tabs = [
    { value: "info", label: "Informations", icon: Info },
    { value: "plan", label: "Abonnement", icon: Package },
    ...(isAdmin ? [{ value: "admin", label: "Admin", icon: Settings }] : [])
  ];

  return (
    <div className="mx-auto">


      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center gap-2">
            <X className="h-4 w-4" />
            {error}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>

        <Tabs defaultValue="info" className="w-full">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Vertical Tabs */}
            <TabsList className="flex flex-col md:w-48 w-full h-fit p-1.5 bg-muted/50 rounded-lg border border-border/50">
              {tabs.map(tab => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="flex items-center gap-3 w-full justify-start px-4 py-2.5 h-11 rounded-md text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <tab.icon className="h-4 w-4 shrink-0" />
                  <span>{tab.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Content Area */}
            <div className="flex-1 min-h-[400px]">
              <TabsContent value="info" className="mt-0">
                <GroupeInfoSection
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleSelectChange={handleSelectChange}
                />
              </TabsContent>

              <TabsContent value="plan" className="mt-0">
                {subscriptionConfig ? (
                  <GroupePlanSection
                    subscriptionConfig={subscriptionConfig}
                    selectedPlanType={selectedPlanType}
                    selectedTierId={selectedTierId}
                    onPlanSelect={handlePlanSelect}
                  />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Chargement des plans...
                  </div>
                )}
              </TabsContent>

              {isAdmin && (
                <TabsContent value="admin" className="mt-0">
                  <GroupeAdminSection
                    formData={formData}
                    subscriptionConfig={subscriptionConfig}
                    selectedPlanType={selectedPlanType}
                    selectedTierId={selectedTierId}
                    groupeId={groupeId}
                    isUpdating={isUpdatingSubscription}
                    handleInputChange={handleInputChange}
                    handleSubscriptionUpdate={handleSubscriptionUpdate}
                  />
                </TabsContent>
              )}
            </div>
          </div>
        </Tabs>


        {/* Actions */}
        <div className="flex justify-between pt-6">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={isPending}
          >
            <X className="h-4 w-4 mr-2" />
            Annuler
          </Button>

          <Button
            type="submit"
            disabled={isPending || !formData.name}
            className="min-w-[160px]"
          >
            {isPending ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                En cours...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4" />
                Sauvegarder
              </div>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

